import { createContext, useContext, useEffect, useState } from "react";
import { jsx } from "react/jsx-runtime";
import process from "node:process";
import { Box, useStdout } from "ink";
//#region src/screen/provider.tsx
const KiddContext = createContext(null);
/**
* Provider that injects the kidd screen context into the React tree.
* Screens rendered by the kidd runtime are automatically wrapped in
* this provider.
*
* @private
* @param props - Provider props containing the context value and children.
* @returns A React element wrapping children with the kidd context.
*/
function KiddProvider({ children, value }) {
	return /* @__PURE__ */ jsx(KiddContext.Provider, {
		value,
		children
	});
}
/**
* Access the command context from within a screen component.
*
* Returns a {@link ScreenContext} containing data properties (`args`,
* `config`, `meta`, `store`), React-backed I/O (`log`, `spinner`),
* and middleware-decorated properties (`auth`, `http`, `report`, etc.).
*
* `log` and `spinner` are automatically swapped with screen-safe
* implementations that render through the `<Output />` component.
* Middleware properties like `report` are also swapped when present.
*
* @returns The current screen context.
*/
function useScreenContext() {
	const ctx = useContext(KiddContext);
	if (!ctx) throw new Error("useScreenContext must be used inside a screen() component");
	return ctx;
}
//#endregion
//#region src/ui/layout/fullscreen.tsx
/** ANSI: switch to the alternate screen buffer. */
const ENTER_ALT_SCREEN = "\x1B[?1049h";
/**
* ANSI: return to the normal screen buffer.
*/
const LEAVE_ALT_SCREEN = "\x1B[?1049l";
/**
* ANSI: clear the entire screen.
*/
const CLEAR_SCREEN = "\x1B[2J";
/**
* ANSI: move the cursor to the top-left corner.
*/
const CURSOR_HOME = "\x1B[H";
/** ANSI: hide the cursor. */
const HIDE_CURSOR = "\x1B[?25l";
/** ANSI: show the cursor. */
const SHOW_CURSOR = "\x1B[?25h";
const FullScreenContext = createContext(null);
/**
* Read terminal dimensions and fullscreen state from the nearest
* `<FullScreen>` ancestor.
*
* When used outside a `<FullScreen>`, falls back to `process.stdout`
* dimensions with `isFullScreen: false`.
*
* @returns Current fullscreen state including terminal dimensions.
*/
function useFullScreen() {
	const ctx = useContext(FullScreenContext);
	if (ctx) return ctx;
	return {
		columns: process.stdout.columns ?? 80,
		isFullScreen: false,
		rows: process.stdout.rows ?? 24
	};
}
/**
* Track terminal dimensions with resize events.
*
* Standalone hook that does not depend on `<FullScreen>`. Listens for
* the `resize` event on `process.stdout` and returns the current
* columns and rows.
*
* @returns Current terminal size.
*/
function useTerminalSize() {
	const [size, setSize] = useState(readTerminalSize);
	useEffect(() => {
		/**
		* Handle terminal resize events.
		*
		* @private
		*/
		function handleResize() {
			setSize(readTerminalSize());
		}
		process.stdout.on("resize", handleResize);
		return () => {
			process.stdout.off("resize", handleResize);
		};
	}, []);
	return size;
}
/**
* Enter the terminal's alternate screen buffer while mounted.
*
* Wraps children in a `<Box>` that fills the terminal height. On mount,
* writes the ANSI alternate-screen-enter sequence; on unmount (or
* process exit), writes the leave sequence. All cleanup handlers are
* idempotent.
*
* @param props - Component props including children and optional cursor hiding.
* @returns A React element that renders children in fullscreen mode.
*/
function FullScreen({ children, hideCursor = false }) {
	const { write } = useStdout();
	const { columns, rows } = useTerminalSize();
	useEffect(() => {
		write(ENTER_ALT_SCREEN);
		if (hideCursor) write(HIDE_CURSOR);
		const handlers = createCleanupHandlers({
			hideCursor,
			write
		});
		handlers.register();
		return () => {
			handlers.unregister();
			writeLeaveSequence({
				hideCursor,
				write
			});
		};
	}, [write, hideCursor]);
	const state = Object.freeze({
		columns,
		isFullScreen: true,
		rows
	});
	return /* @__PURE__ */ jsx(FullScreenContext.Provider, {
		value: state,
		children: /* @__PURE__ */ jsx(Box, {
			minHeight: rows,
			children
		})
	});
}
/**
* Read the current terminal size from `process.stdout`.
*
* @private
* @returns Terminal dimensions with sensible defaults.
*/
function readTerminalSize() {
	return Object.freeze({
		columns: process.stdout.columns ?? 80,
		rows: process.stdout.rows ?? 24
	});
}
/**
* Write the leave-fullscreen ANSI sequences.
*
* @private
* @param opts - Options for the write.
*/
function writeLeaveSequence({ hideCursor, write }) {
	write(CLEAR_SCREEN);
	write(CURSOR_HOME);
	if (hideCursor) write(SHOW_CURSOR);
	write(LEAVE_ALT_SCREEN);
}
/**
* Create signal and process-exit handlers that leave the alternate screen
* buffer on abnormal termination. All handlers are idempotent.
*
* @private
* @param config - Configuration including write function and cursor visibility.
* @returns Frozen object with `register` and `unregister` methods.
*/
function createCleanupHandlers(config) {
	/**
	* Handle SIGINT / SIGTERM — leave alt screen and re-raise.
	*
	* @private
	*/
	function handleSignal(signal) {
		writeLeaveSequence(config);
		process.kill(process.pid, signal);
	}
	/**
	* Handle process.exit — leave alt screen synchronously.
	*
	* @private
	*/
	function handleExit() {
		writeLeaveSequence(config);
	}
	return Object.freeze({
		register: () => {
			process.on("SIGINT", handleSignal);
			process.on("SIGTERM", handleSignal);
			process.on("exit", handleExit);
		},
		unregister: () => {
			process.off("SIGINT", handleSignal);
			process.off("SIGTERM", handleSignal);
			process.off("exit", handleExit);
		}
	});
}
//#endregion
//#region src/screen/output/store-key.ts
/**
* Symbol key used to attach the {@link OutputStore} to the screen context.
*
* @private
*/
const OUTPUT_STORE_KEY = Symbol("kidd.outputStore");
/**
* Return a new context with the {@link OutputStore} attached via a hidden
* Symbol key. Does not mutate the original context.
*
* @param options - The injection options.
* @returns A new record containing all original entries plus the store.
*/
function injectOutputStore({ ctx, store }) {
	return {
		...ctx,
		[OUTPUT_STORE_KEY]: store
	};
}
/**
* Retrieve the {@link OutputStore} from a screen context.
*
* Asserts that the store exists — this is a framework invariant
* guaranteed by `screen()` calling {@link injectOutputStore}.
*
* @param ctx - The screen context to read from.
* @returns The output store attached by {@link injectOutputStore}.
*/
function getOutputStore(ctx) {
	const store = ctx[OUTPUT_STORE_KEY];
	if (store === void 0) throw new Error("OutputStore not found on ScreenContext. Ensure useOutputStore is called within a screen() context.");
	return store;
}
//#endregion
export { useFullScreen as a, useScreenContext as c, LEAVE_ALT_SCREEN as i, injectOutputStore as n, useTerminalSize as o, FullScreen as r, KiddProvider as s, getOutputStore as t };

//# sourceMappingURL=store-key-fUtc4MNq.js.map